// Parameter Selection Component for Pipeline Analysis
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ParamDetailModal from './param-detail-modal';
import './parameter-selection.css';

function ParameterSelection({ 
  selectedJob, 
  resultsData, 
  resultsSearch, 
  setResultsSearch, 
  resultsTypeFilter, 
  setResultsTypeFilter 
}) {
  const [filteredResults, setFilteredResults] = useState([]);
  const [displayedResults, setDisplayedResults] = useState([]);
  const [animation, setAnimation] = useState(false);
  const [visibleCount, setVisibleCount] = useState(20);
  const resultsContainerRef = useRef(null);

  // Function to check if we need to load more items when scrolling
  const handleScroll = useCallback(() => {
    if (!resultsContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = resultsContainerRef.current;
    // If we're near the bottom (within 50px), load more items
    if (scrollTop + clientHeight >= scrollHeight - 50 && displayedResults.length < filteredResults.length) {
      setVisibleCount(prev => Math.min(prev + 20, filteredResults.length));
    }
  }, [displayedResults.length, filteredResults.length]);

  // Search across all columns
  const searchAllColumns = (item, query) => {
    if (!query) return true;
    query = query.toLowerCase();
    
    // Search in basic fields
    if (
      (item.gene_symbol && item.gene_symbol.toLowerCase().includes(query)) ||
      (item.biomarker_description && item.biomarker_description.toLowerCase().includes(query)) ||
      (item.alteration && item.alteration.toLowerCase().includes(query)) ||
      (item.position && item.position.toLowerCase().includes(query)) ||
      (item.score_or_frequency && item.score_or_frequency.toString().toLowerCase().includes(query)) ||
      (item.drug_name_nda && item.drug_name_nda.toLowerCase().includes(query)) ||
      (item.entry_type && item.entry_type.toLowerCase().includes(query))
    ) {
      return true;
    }
    
    // Search in trial info if available
    if (item.entry_type === 'TRIAL' && item.trial_biomarker_info) {
      return (
        (item.trial_biomarker_info.nctid && item.trial_biomarker_info.nctid.toLowerCase().includes(query)) ||
        (item.trial_biomarker_info.phase && item.trial_biomarker_info.phase.toString().toLowerCase().includes(query)) ||
        (item.trial_biomarker_info.description && item.trial_biomarker_info.description.toLowerCase().includes(query))
      );
    }
    
    return false;
  };

  // Sort by tier (highest first) and filter results
  useEffect(() => {
    if (!resultsData) {
      setFilteredResults([]);
      setDisplayedResults([]);
      return;
    }
    
    const filtered = resultsData.filter(res => {
      const matchesType = resultsTypeFilter === 'all' || res.entry_type === resultsTypeFilter;
      const matchesSearch = searchAllColumns(res, resultsSearch.toLowerCase());
      return matchesType && matchesSearch;
    });
    
    // Sort first by type order (if All Types is selected), then by tier
    const sorted = [...filtered].sort((a, b) => {
      // If 'all' types is selected, sort by type order: BIOMARKER, DRUG, TRIAL
      if (resultsTypeFilter === 'all') {
        // Define type priority (lower number = higher priority)
        const getTypePriority = (type) => {
          switch (type) {
            case 'BIOMARKER': return 1;
            case 'DRUG': return 2;
            case 'TRIAL': return 3;
            default: return 4;
          }
        };
        
        const typePriorityA = getTypePriority(a.entry_type);
        const typePriorityB = getTypePriority(b.entry_type);
        
        if (typePriorityA !== typePriorityB) {
          return typePriorityA - typePriorityB;
        }
      }
      
      // After sorting by type (or if specific type is selected), sort by tier
      // If both have tiers, compare them (lower tier number is higher priority)
      if (a.tier !== undefined && b.tier !== undefined) {
        return a.tier - b.tier;
      }
      // If only a has tier, a comes first
      if (a.tier !== undefined) return -1;
      // If only b has tier, b comes first
      if (b.tier !== undefined) return 1;
      // If neither has tier, maintain original order
      return 0;
    });
    
    setFilteredResults(sorted);
    // Reset visible count when filter changes
    setVisibleCount(20);
    setAnimation(true);
    const timer = setTimeout(() => setAnimation(false), 800);
    return () => clearTimeout(timer);
  }, [resultsData, resultsSearch, resultsTypeFilter]);

  // Update displayed results when filteredResults or visibleCount changes
  useEffect(() => {
    setDisplayedResults(filteredResults.slice(0, visibleCount));
  }, [filteredResults, visibleCount]);

  // Add scroll event listener
  useEffect(() => {
    const resultsContainer = resultsContainerRef.current;
    if (resultsContainer) {
      resultsContainer.addEventListener('scroll', handleScroll);
      return () => {
        resultsContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  // State for modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // 'view' or 'edit'
  const [selectedItem, setSelectedItem] = useState(null);
  const [fieldToEdit, setFieldToEdit] = useState('');

  // Action handlers for results
  const handleRemoveResult = (index) => {
    console.log('[Results] remove row', index);
  };

  const handleViewDetails = (item) => {
    setSelectedItem(item);
    setModalMode('view');
    setModalOpen(true);
  };

  const handleEditField = (item, field) => {
    setSelectedItem(item);
    setFieldToEdit(field);
    setModalMode('edit');
    setModalOpen(true);
  };
  
  const handleSaveChanges = async (patientId, editedData) => {
    try {
      // Use the Fetch API as per project requirements
      const response = await fetch('https://service8.szapfs.org/api/generiskpro/update-parameter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientId: patientId,
          itemId: editedData.id || editedData.uuid,
          updatedData: editedData
        })
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();
      console.log('Update successful:', data);
      
      // Here you would typically refresh the data
      // For now, we'll just log it
      return data;
    } catch (error) {
      console.error('Error updating parameter:', error);
      throw error;
    }
  };

  return (
    <div className="param-container">
      {resultsData && resultsData.length > 0 ? (
        <>
          <div className="param-controls">
            
            <div className="param-search-wrapper">
    
              <input
                type="text"
                placeholder="Search across all fields..."
                value={resultsSearch}
                onChange={e => setResultsSearch(e.target.value)}
                className="param-search-input"
              />
              <i className="fas fa-search param-search-icon"></i>
            </div>
            <div className="param-filter-wrapper">
              <select 
                value={resultsTypeFilter} 
                onChange={e => setResultsTypeFilter(e.target.value)}
                className="param-filter-select"
              >
                <option value="all">All Types</option>
                <option value="BIOMARKER">Biomarker</option>
                <option value="DRUG">Drug</option>
                <option value="TRIAL">Trial</option>
              </select>
              <i className="fas fa-filter param-filter-icon"></i>
            </div>
          </div>

          <div className="param-results-list" ref={resultsContainerRef}>
            {filteredResults.length === 0 ? (
              <div className="param-no-results">
                <i className="fas fa-search"></i>
                <p>No results match your search criteria</p>
              </div>
            ) : (
              <div className="param-results-grid">
                {displayedResults.map((res, rIndex) => (
                  <div 
                    className={`param-result-tile ${animation ? 'param-anim-fadeIn' : ''}`} 
                    key={rIndex}
                    style={{ animationDelay: `${rIndex * 0.05}s` }}
                  >
                    <div className="param-result-header">
                      <div className="param-result-type-wrapper">
                        <div className="param-result-type-container">
                          <button 
                            className="param-info-btn" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(res);
                            }}
                            title="View details"
                          >
                            <i className="fas fa-info-circle"></i>
                          </button>
                          <span className={`param-result-type param-result-type-${res.entry_type.toLowerCase()}`}>
                            {res.entry_type === 'BIOMARKER' && <i className="fas fa-dna"></i>}
                            {res.entry_type === 'DRUG' && <i className="fas fa-pills"></i>}
                            {res.entry_type === 'TRIAL' && <i className="fas fa-flask"></i>}
                            {res.entry_type}
                          </span>
                        </div>
                        {res.tier !== null ? (
                          <div className="param-tier-container">
                            <button 
                              className={`param-tier-badge tier-${res.tier}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              Tier {res.tier}
                              <i 
                                className="fas fa-pen param-edit-icon" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditField(res, 'tier');
                                }}
                              ></i>
                            </button>
                          </div>
                        ) : null}
                      </div>
                      <div className="param-result-actions">
                        <button 
                          className="param-action-btn" 
                          onClick={() => handleRemoveResult(rIndex)} 
                          title="Remove"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    </div>

                    <div className="param-result-body">
                      {res.gene_symbol && (
                        <div className="param-result-field">
                          <span className="param-field-label">Gene:</span>
                          <div className="param-field-value-container">
                            <span className="param-field-value param-gene">{res.gene_symbol}</span>
                            <i 
                              className="fas fa-pen param-edit-icon" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditField(res, 'gene_symbol');
                              }}
                            ></i>
                          </div>
                        </div>
                      )}
                      
                      {res.biomarker_description && (
                        <div className="param-result-field">
                          <span className="param-field-label">Description:</span>
                          <div className="param-field-value-container">
                            <span className="param-field-value">{res.biomarker_description}</span>
                            <i 
                              className="fas fa-pen param-edit-icon" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditField(res, 'biomarker_description');
                              }}
                            ></i>
                          </div>
                        </div>
                      )}
                      
                      {res.alteration && (
                        <div className="param-result-field">
                          <span className="param-field-label">Alteration:</span>
                          <div className="param-field-value-container">
                            <span className="param-field-value">{res.alteration}</span>
                            <i 
                              className="fas fa-pen param-edit-icon" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditField(res, 'alteration');
                              }}
                            ></i>
                          </div>
                        </div>
                      )}
                      
                      {res.position && (
                        <div className="param-result-field">
                          <span className="param-field-label">Position:</span>
                          <div className="param-field-value-container">
                            <span className="param-field-value">{res.position}</span>
                            <i 
                              className="fas fa-pen param-edit-icon" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditField(res, 'position');
                              }}
                            ></i>
                          </div>
                        </div>
                      )}
                      
                      {res.score_or_frequency && (
                        <div className="param-result-field">
                          <span className="param-field-label">Score/Freq:</span>
                          <div className="param-field-value-container">
                            <span className="param-field-value">{res.score_or_frequency}</span>
                            <i 
                              className="fas fa-pen param-edit-icon" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditField(res, 'score_or_frequency');
                              }}
                            ></i>
                          </div>
                        </div>
                      )}
                      
                      {res.drug_name_nda && (
                        <div className="param-result-field">
                          <span className="param-field-label">Drug:</span>
                          <div className="param-field-value-container">
                            <span className="param-field-value param-drug">{res.drug_name_nda}</span>
                            <i 
                              className="fas fa-pen param-edit-icon" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditField(res, 'drug_name_nda');
                              }}
                            ></i>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {res.entry_type === 'TRIAL' && res.trial_biomarker_info && (
                      <div className="param-trial-info">
                        <div className="param-trial-header">
                          <span className="param-trial-nctid">
                            <i className="fas fa-tag"></i> {res.trial_biomarker_info.nctid}
                          </span>
                          <span className="param-trial-phase">
                            Phase {res.trial_biomarker_info.phase}
                          </span>
                        </div>
                        <div className="param-trial-desc">{res.trial_biomarker_info.description}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {filteredResults.length > visibleCount && (
              <div className="param-load-more">
                <div className="param-loading-indicator">
                  <div className="param-loading-dot"></div>
                  <div className="param-loading-dot"></div>
                  <div className="param-loading-dot"></div>
                </div>
                <span>Scroll for more results</span>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="param-placeholder">
          <i className="fas fa-clipboard-list"></i>
          <p>{selectedJob ? 'No results data available for this job' : 'Select a job to view parameters'}</p>
        </div>
      )}
      
      {/* Modal for viewing details and editing fields */}
      <ParamDetailModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        data={selectedItem}
        mode={modalMode}
        onSave={handleSaveChanges}
        fieldToEdit={fieldToEdit}
        patientId={selectedJob?.patientId}
      />
    </div>
  );
}

export default ParameterSelection;
